import type { ComponentPropsWithRef, ReactNode } from 'react';
import type { LinkProps } from 'react-router';

import { Button as BaseButton, type ButtonProps } from '@base-ui/react';
import { use } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';

import { clsx } from '~/styles/utils';

import type { IconName } from '../Icon/Icon';
import type { FloatingActionEmphasis } from './HeaderActions.utils';

import { getSharedButtonClassNames } from '../Button/Button.utils';
import { Icon } from '../Icon/Icon';
import { Spinner } from '../Spinner/Spinner';
import { floatingActionStyle, floatingMenuTriggerStyle, rotatedIconStyle, secondaryFloatingActionStyle } from './FloatingAction.css';
import { FloatingRootPortalContext } from './HeaderActions.context';

type SharedFloatingActionProps = {
  'aria-label': string;
  children?: never; // Floating actions are icon-only
  className?: never; // This is meant for the header action - it makes no sense to share with the floating action
  emphasis: FloatingActionEmphasis;
  icon: IconName;
  style?: never; // This is meant for the header action - it makes no sense to share with the floating action
};

// Floating Action

type FloatingActionProps = ButtonProps &
  SharedFloatingActionProps & {
    loading?: boolean;
  };

export const FloatingAction = (props: FloatingActionProps) => {
  const { emphasis, icon, loading, ...delegated } = props;

  return (
    <FloatingActionPortal>
      <BaseButton
        {...delegated}
        className={clsx(
          getSharedButtonClassNames({ emphasis, icon }),
          floatingActionStyle,
          emphasis === 'secondary' && secondaryFloatingActionStyle,
        )}
        disabled={loading || delegated.disabled}
      >
        {loading ? <Spinner size={24} /> : <Icon name={icon} />}
      </BaseButton>
    </FloatingActionPortal>
  );
};

// Floating Menu Trigger

type FloatingMenuTriggerProps = Omit<ButtonProps, 'children' | 'className' | 'style'> & {
  'aria-label': string;
  icon: IconName;
  loading?: boolean;
};

export const FloatingMenuTrigger = ({ icon, loading, ...delegated }: FloatingMenuTriggerProps) => {
  return (
    <BaseButton
      {...delegated}
      className={clsx(getSharedButtonClassNames({ emphasis: 'primary', icon }), floatingActionStyle, floatingMenuTriggerStyle)}
      disabled={loading || delegated.disabled}
    >
      {loading ? <Spinner size={24} /> : <Icon name={icon} />}
      <Icon className={rotatedIconStyle} name="MoreVert" size={20} />
    </BaseButton>
  );
};

// Floating Link

type FloatingLinkProps = LinkProps & SharedFloatingActionProps;

export const FloatingLinkAction = (props: FloatingLinkProps) => {
  const { emphasis, icon, ...delegated } = props;

  return (
    <FloatingActionPortal>
      <Link {...delegated} className={clsx(getSharedButtonClassNames({ emphasis, icon }), floatingActionStyle)}>
        <Icon name={icon} />
      </Link>
    </FloatingActionPortal>
  );
};

// Floating External Link

type FloatingExternalLinkProps = ComponentPropsWithRef<'a'> & SharedFloatingActionProps;

export const FloatingExternalLinkAction = (props: FloatingExternalLinkProps) => {
  const { emphasis, icon, ...delegated } = props;

  return (
    <FloatingActionPortal>
      <a
        {...delegated}
        className={clsx(getSharedButtonClassNames({ emphasis, icon }), floatingActionStyle)}
        rel={delegated.rel ?? 'noreferrer'}
        target={delegated.target ?? '_blank'}
      >
        <Icon name={icon} />
      </a>
    </FloatingActionPortal>
  );
};

/** Puts its children next to the other floating actions. */
const FloatingActionPortal = ({ children }: { children: ReactNode }) => {
  const portal = use(FloatingRootPortalContext);

  if (!portal) return null;

  return createPortal(children, portal);
};
